import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner, Toaster } from '../../components/common';
import { MatchCard } from '../../components/matches';
import { MATCH_STATUS, type Match } from '../../types/match.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import {
  useOnboardingMatches,
  useUpdateMatchStatus,
} from '../../hooks/useMatch.ts';
import { routes } from '../../lib/routes';
import toast from 'react-hot-toast';
import { DatabaseWarning, HomeSimple, PageEdit } from 'iconoir-react';

const OnboardingMatches = () => {
  const navigate = useNavigate();
  const { checkStatus } = useAuth();
  const { data: matchesData, isLoading, isSuccess } = useOnboardingMatches();
  const { mutateAsync: updateMatchStatus } = useUpdateMatchStatus();

  const updateMatchStatusMutation = async (id: string, status: string) => {
    let flag = false;
    await updateMatchStatus(
      { match_id: id, status },
      {
        onSuccess: () => {
          flag = true;
        },
        onError: error => {
          toast.custom(t => (
            <Toaster message={error?.message} type='error' id={t.id} />
          ));
          flag = false;
        },
      }
    );
    return flag;
  };

  const onApprove = async (match: Match) => {
    const success = await updateMatchStatusMutation(
      match.id,
      MATCH_STATUS.APPROVED
    );
    if (!success) {
      return;
    }

    toast.custom(t => (
      <Toaster
        message={`You have approved ${match.other_user.name}!`}
        type='success'
        id={t.id}
      />
    ));
  };

  const onReject = async (match: Match, open: boolean) => {
    if (open) {
      return;
    }

    const success = await updateMatchStatusMutation(
      match.id,
      MATCH_STATUS.REJECTED
    );
    if (!success) {
      return;
    }

    toast.custom(t => (
      <Toaster
        message={`You have passed ${match.other_user.name}!`}
        type='success'
        id={t.id}
      />
    ));
  };

  useEffect(() => {
    if (isSuccess) {
      checkStatus();
    }
  }, [isSuccess]);

  if (isLoading) {
    return (
      <div className='container-xl px-4 md:px-8'>
        <div className='py-[3.1875rem] flex justify-between items-center'>
          <div className='w-full flex items-center justify-center h-[calc(100dvh-15.2rem)]'>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  // If no matches are found show this placeholder
  if (!isLoading && matchesData?.result?.matches.length < 1) {
    return (
      <div className='container-xl px-4 md:px-8'>
        <div className='py-[3.1875rem] sm:flex justify-between items-center'>
          <div className='sm:mb-0 mb-3'>
            <h5 className='mb-1 !font-medium text-heading-500'>
              {matchesData?.result?.matches_found
                ? 'You’re up to date! 🚀'
                : 'No Matches found'}
            </h5>
          </div>
          <div className='flex items-center gap-2'>
            {matchesData?.result?.matches_found && (
              <Button variant='outline' onClick={() => navigate(routes.home)}>
                <HomeSimple className='w-6 h-6' />
                Go to dashboard
              </Button>
            )}
            <Button
              color='dark'
              onClick={() => navigate(routes.onboardingSummary)}
            >
              <PageEdit className='w-6 h-6' />
              Update questionnaire
            </Button>
          </div>
        </div>
        <div className='my-15 flex flex-col justify-center items-center'>
          <DatabaseWarning className='w-15 h-15 text-purple-500 mb-4' />
          <p className='text-body-400 mb-0 sm:max-w-1/2 text-center font-medium mb-6'>
            {matchesData?.result?.matches_found
              ? "You've reviewed all your matches for now. Your AI agent is scanning your network for the next great introduction. Check back soon!"
              : "We didn't find a match right now, but with a few updates to your questionnaire, we'll be one step closer to finding your ideal connection."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='container-xl px-4 md:px-8'>
      <div className='py-[3.1875rem] sm:flex justify-between items-center'>
        <div className='sm:mb-0 mb-3'>
          <h5 className='mb-1 !font-medium text-heading-500'>Matches found</h5>
          <p className='text-sm text-body-400 font-medium mb-0'>
            Recent matches found by your AI agent
          </p>
        </div>
        <Button color='dark' onClick={() => navigate(routes.home)}>
          Go to dashboard
        </Button>
      </div>

      <div className='h-[calc(100dvh-15.2rem)] overflow-y-auto'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-6 pb-8'>
          {matchesData?.result?.matches.map((match: Match) => (
            <MatchCard
              key={match.id}
              match={match}
              onApprove={() => onApprove(match)}
              onReject={open => onReject(match, open)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingMatches;
